import React, { createContext, useContext, useState, useEffect } from 'react';

const JubileeAuthContext = createContext(null);

const DEMO_USERS = [
  {
    email: 'admin@jemparc.org',
    password: 'admin123',
    name: 'Fr. Archivist & Director',
    role: 'admin',
    designation: 'Chief Province Archivist & Admin',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80',
  },
  {
    email: 'archivist@jemparc.org',
    password: 'archive123',
    name: 'Archive Researcher SJ',
    role: 'archivist',
    designation: 'Digital Heritage Curator',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
  },
];

const AUTH_STORAGE_KEY = 'jemparc_jubilee_auth_session';

export const JubileeAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    try {
      const session = localStorage.getItem(AUTH_STORAGE_KEY);
      if (session) {
        setCurrentUser(JSON.parse(session));
      }
    } catch (e) {
      console.error('Failed to load Jubilee auth session', e);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    // Check against demo accounts first
    const matched = DEMO_USERS.find(
      (u) => u.email.toLowerCase() === email.trim().toLowerCase() && u.password === password
    );

    let sessionUser = null;
    if (matched) {
      const { password: _, ...userSafe } = matched;
      sessionUser = { ...userSafe, token: `jwt-sim-${Date.now()}` };
    } else if (email && password) {
      // Allow custom email login with archivist role
      sessionUser = {
        email: email.trim(),
        name: email.split('@')[0].toUpperCase(),
        role: email.toLowerCase().includes('admin') ? 'admin' : 'archivist',
        designation: 'Archival Staff',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80',
        token: `jwt-sim-${Date.now()}`,
      };
    } else {
      throw new Error('Please provide valid credentials');
    }

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(sessionUser));
    setCurrentUser(sessionUser);
    return sessionUser;
  };

  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setCurrentUser(null);
  };

  const isAuthenticated = !!currentUser;
  const role = currentUser?.role || 'guest';
  const canUpload = isAuthenticated && (role === 'admin' || role === 'archivist');
  const canManage = isAuthenticated && (role === 'admin' || role === 'archivist');
  const canDelete = isAuthenticated && role === 'admin';

  return (
    <JubileeAuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        role,
        authLoading,
        login,
        logout,
        canUpload,
        canManage,
        canDelete,
        demoUsers: DEMO_USERS,
      }}
    >
      {children}
    </JubileeAuthContext.Provider>
  );
};

export const useJubileeAuth = () => {
  const context = useContext(JubileeAuthContext);
  if (!context) {
    throw new Error('useJubileeAuth must be used within a JubileeAuthProvider');
  }
  return context;
};
